<template>
  <div>
    <drawer
      :title="$t('labels.waysAndCycles')"
      :open="waysSubview == 'waysAndCycles'"
      @open="waysSubview = 'waysAndCycles'"
      @close="waysSubview = null"
    >
      <ways-menu />
    </drawer>
    <drawer
      :title="$t('labels.impulseTracking')"
      :open="waysSubview == 'impulseTracking'"
      @open="waysSubview = 'impulseTracking'"
      @close="
        activeWayId = null;
        waysSubview = null;
      "
    >
      <drawer
        v-for="way of ways"
        :key="way.id"
        :title="way.title"
        :name="'' + way.id"
        :openname="openWay ?? undefined"
        :nested="true"
        @change="saveOpenWay({ name: $event.name })"
        @open="showWay(way)"
        @close="hideWay(way)"
      >
        <div class="label">
          {{ $t("labels.title") }}
        </div>
        <div class="line_value line_value_doc">
          <input-text
            :value="way.title"
            placeholder="Titel"
            @update:value="updateWay(way, 'title', $event)"
          />
          <button
            class="button-icon"
            type="button"
            :title="$t('labels.delete')"
            @click="deleteWay(way)"
          >
            <lucide-icon name="trash-2" />
          </button>
          <doc-anchor :object="way" prop="title" />
        </div>
        <way-list :way-id="way.id" class="mt-2" />
      </drawer>
      <button class="button mt-4" type="button" @click="createWay">
        {{ $t("labels.startImpactChain") }}
      </button>
    </drawer>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import Drawer from "@core/ui/DrawerBlock.vue";
import LucideIcon from "@core/ui/LucideIcon.vue";
import InputText from "@core/input/InputText.vue";
import DocAnchor from "@editor/ui/DocumentationAnchor.vue";

import WayList from "@editor/items/way/WayList.vue";
import WaysMenu from "@editor/items/way/WaysMenu.vue";

import { useInspectorView } from "@editor/views/inspector/inspectorView.store";
import { useWayStore } from "@editor/items/way/ways.store";

import type { WayItem } from "@editor/items/way/WayItem";

const { t } = useI18n();

const wayStore = useWayStore();

const waysSubview = computed({
  get() {
    return useInspectorView().waysSubview;
  },
  set(value: "impulseTracking" | "waysAndCycles" | null) {
    useInspectorView().set({ waysSubview: value });
  }
});

const ways = computed(() => {
  return wayStore.orderedItems;
});

const activeWayId = computed({
  get() {
    return wayStore.activeId;
  },
  set(value: number | null) {
    wayStore.setActive(value);
  }
});

const openWay = computed(() => {
  if (activeWayId.value == null) return null;
  return activeWayId.value + "";
});

function updateWay<K extends keyof WayItem>(
  way: WayItem,
  key: K,
  value: WayItem[K]
) {
  wayStore.update({
    id: way.id,
    [key]: value
  });
}

async function createWay() {
  const way = await wayStore.create({
    title: t("objectNames.impactChain") + " " + (ways.value.length + 1)
  });
  activeWayId.value = way.id;
}

function deleteWay(way: WayItem) {
  wayStore.delete(way);
}

function saveOpenWay({ name }: { name: string | undefined }) {
  if (name == null) {
    activeWayId.value = null;
    return;
  }

  // cut of "way"
  const id = parseInt(name);
  if (activeWayId.value === id) {
    activeWayId.value = null;
    return;
  }
  activeWayId.value = id;
}

function showWay(way: WayItem) {
  wayStore.update({
    id: way.id,
    visible: true
  });
}

function hideWay(way: WayItem) {
  wayStore.update({
    id: way.id,
    visible: false
  });
}
</script>

<style></style>
